import React, { useMemo } from 'react';

/**
 * Rendu Markdown minimal, sans dépendance externe.
 *
 * Couvre exactement ce que l'administrateur peut saisir depuis le panel :
 * titres, gras, italique, listes, tableaux, liens et paragraphes.
 * Le HTML brut est échappé avant tout traitement : un contenu collé depuis
 * un traitement de texte ne peut pas injecter de balise.
 */
const echapper = (texte) =>
  texte
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const enLigne = (texte) =>
  echapper(texte)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*]+?)\*/g, '$1<em>$2</em>')
    .replace(/`(.+?)`/g, '<code class="px-1 py-0.5 bg-gray-100 rounded text-sm">$1</code>')
    .replace(
      /\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline">$1</a>'
    )
    .replace(
      /(^|\s)(https?:\/\/[^\s<]+)/g,
      '$1<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline break-all">$2</a>'
    );

const convertir = (markdown) => {
  const lignes = (markdown || '').split('\n');
  const sortie = [];
  let listeOuverte = false;
  let tableau = null;

  const fermerListe = () => {
    if (listeOuverte) {
      sortie.push('</ul>');
      listeOuverte = false;
    }
  };

  const fermerTableau = () => {
    if (tableau) {
      sortie.push(tableau.join('') + '</tbody></table></div>');
      tableau = null;
    }
  };

  lignes.forEach((ligne) => {
    const l = ligne.trim();

    if (!l) {
      fermerListe();
      fermerTableau();
      return;
    }

    // Tableaux
    if (l.startsWith('|') && l.endsWith('|')) {
      const cellules = l.slice(1, -1).split('|').map((c) => c.trim());
      if (cellules.every((c) => /^:?-+:?$/.test(c))) return; // ligne de séparation
      if (!tableau) {
        tableau = [
          '<div class="overflow-x-auto my-4"><table class="w-full text-sm border-collapse"><thead><tr>' +
            cellules
              .map(
                (c) =>
                  `<th class="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold">${enLigne(c)}</th>`
              )
              .join('') +
            '</tr></thead><tbody>',
        ];
      } else {
        tableau.push(
          '<tr>' +
            cellules
              .map((c) => `<td class="border border-gray-200 px-3 py-2 align-top">${enLigne(c)}</td>`)
              .join('') +
            '</tr>'
        );
      }
      return;
    }
    fermerTableau();

    // Titres
    const titre = l.match(/^(#{1,4})\s+(.*)$/);
    if (titre) {
      fermerListe();
      const niveau = titre[1].length;
      const classes = {
        1: 'text-3xl font-bold text-gray-900 mt-8 mb-4',
        2: 'text-2xl font-bold text-gray-900 mt-8 mb-3',
        3: 'text-xl font-semibold text-gray-900 mt-6 mb-2',
        4: 'text-lg font-semibold text-gray-800 mt-4 mb-2',
      }[niveau];
      sortie.push(`<h${niveau} class="${classes}">${enLigne(titre[2])}</h${niveau}>`);
      return;
    }

    // Listes
    if (/^[-*]\s+/.test(l)) {
      if (!listeOuverte) {
        sortie.push('<ul class="list-disc pl-6 space-y-1 my-3 text-gray-700">');
        listeOuverte = true;
      }
      sortie.push(`<li>${enLigne(l.replace(/^[-*]\s+/, ''))}</li>`);
      return;
    }
    fermerListe();

    // Séparateur
    if (/^---+$/.test(l)) {
      sortie.push('<hr class="my-6 border-gray-200">');
      return;
    }

    sortie.push(`<p class="text-gray-700 leading-relaxed my-3">${enLigne(l)}</p>`);
  });

  fermerListe();
  fermerTableau();
  return sortie.join('');
};

export const MarkdownContent = ({ content, className = '' }) => {
  const html = useMemo(() => convertir(content), [content]);
  return <div className={className} dangerouslySetInnerHTML={{ __html: html }} />;
};

export default MarkdownContent;
